import type { CliPromptTurnState } from '@buildautomaton/types';
import type { MessageHandlerDeps } from '../../routing/message-handler-deps.js';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { sendPromptQueueClientReport } from '../client-report.js';
import { readPersistedQueue, writePersistedQueue } from '../disk-store.js';
import { hasRunningTurn, pickNextRunnableTurn } from './queue-selection.js';
import { runLocalRevertBeforeQueuedPrompt } from './run-local-revert-before-queued-prompt.js';
import { setRunIdQueueKey } from './run-id-queue-key-map.js';
import type { PromptQueueSnapshotEntries } from './prompt-queue-snapshot-types.js';

export async function startPromptQueueRuns(
  entries: PromptQueueSnapshotEntries,
  deps: MessageHandlerDeps,
): Promise<Set<string>> {
  const report: Record<string, Array<{ turnId: string; cliState: CliPromptTurnState | null }>> = {};
  const started = new Set<string>();
  for (const [queueKey] of entries) {
    const file = await readPersistedQueue(queueKey);
    if (!file || hasRunningTurn(file.turns)) continue;
    const next = pickNextRunnableTurn(file.turns);
    if (!next) continue;
    if (!(await runLocalRevertBeforeQueuedPrompt(next, deps))) {
      next.lastCliState = 'failed';
      await writePersistedQueue(file);
      sendPromptQueueClientReport(deps.getWs(), { [queueKey]: [{ turnId: next.turnId, cliState: 'failed' }] });
      continue;
    }
    next.lastCliState = 'running';
    await writePersistedQueue(file);
    setRunIdQueueKey(next.turnId, queueKey);
    started.add(next.turnId);
    report[queueKey] = [{ turnId: next.turnId, cliState: 'running' }];
    await yieldToEventLoop();
  }
  if (Object.keys(report).length) sendPromptQueueClientReport(deps.getWs(), report);
  return started;
}
